import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiUser, FiMonitor, FiActivity, FiEdit2 } from "react-icons/fi";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const TABS = [
  { key: "sessions", label: "Sessions", icon: FiMonitor },
  { key: "activity", label: "Activity Log", icon: FiActivity },
];

const StaffDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [staff, setStaff] = useState(null);
  const [activeTab, setActiveTab] = useState("sessions");
  const [sessions, setSessions] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [staffRes, sessionsRes, activityRes] = await Promise.all([
          fetch(`${baseUrl}/staff/view/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${baseUrl}/staff/${id}/sessions`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${baseUrl}/staff/${id}/activity`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const staffResult = await staffRes.json();
        const sessionsResult = await sessionsRes.json();
        const activityResult = await activityRes.json();
        if (staffResult.status) setStaff(staffResult.data);
        if (sessionsResult.status) setSessions(sessionsResult.data || []);
        if (activityResult.status) setActivity(activityResult.data || []);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, token]);

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                <FiUser size={20} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{staff?.name || "Staff"}</h2>
                <p className="text-sm text-gray-400 mt-0.5">{staff?.email} · {staff?.roleName || "Unassigned"}</p>
              </div>
            </div>
            <button onClick={() => navigate(`/staff/${id}/edit`)}
              className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2.5 rounded-lg transition">
              <FiEdit2 size={14} /> Edit
            </button>
          </div>

          <div className="flex gap-1 bg-white border border-gray-100 rounded-xl p-1 w-fit shadow-sm">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-5 py-2 text-sm font-medium rounded-lg transition ${
                    activeTab === tab.key ? "bg-blue-600 text-white shadow-sm" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}>
                  <Icon size={14} /> {tab.label}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {activeTab === "sessions" && (
                sessions.length === 0 ? (
                  <div className="py-16 text-center text-gray-400 text-sm">No login sessions yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                          <th className="px-5 py-3">Login At</th>
                          <th className="px-5 py-3">IP Address</th>
                          <th className="px-5 py-3">Device / User Agent</th>
                          <th className="px-5 py-3">Last Activity</th>
                          <th className="px-5 py-3">Logout At</th>
                          <th className="px-5 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {sessions.map((s) => (
                          <tr key={s.id} className="hover:bg-gray-50">
                            <td className="px-5 py-3.5 text-gray-600">{s.loginAt ? new Date(s.loginAt).toLocaleString() : "—"}</td>
                            <td className="px-5 py-3.5 text-gray-600">{s.ipAddress || "—"}</td>
                            <td className="px-5 py-3.5 text-gray-500 text-xs max-w-xs truncate" title={s.userAgent || ""}>{s.userAgent || "—"}</td>
                            <td className="px-5 py-3.5 text-gray-500 text-xs">{s.lastActivityAt ? new Date(s.lastActivityAt).toLocaleString() : "—"}</td>
                            <td className="px-5 py-3.5 text-gray-500 text-xs">{s.logoutAt ? new Date(s.logoutAt).toLocaleString() : "—"}</td>
                            <td className="px-5 py-3.5">
                              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${!s.revoked ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                                {!s.revoked ? "Active" : "Ended"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {activeTab === "activity" && (
                activity.length === 0 ? (
                  <div className="py-16 text-center text-gray-400 text-sm">No activity recorded yet.</div>
                ) : (
                  <ul className="divide-y divide-gray-50">
                    {activity.map((a) => (
                      <li key={a.id} className="px-5 py-4">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm text-gray-700">{a.summary}</p>
                          <span className="text-xs text-gray-400 whitespace-nowrap">{new Date(a.createdAt).toLocaleString()}</span>
                        </div>
                        <span className="inline-block mt-1 text-xs font-mono text-gray-400">{a.action}</span>
                      </li>
                    ))}
                  </ul>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffDetail;
